import { isResource, resolveId } from '@medplum/core'
import type {
  Communication,
  Organization,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { format } from 'date-fns'
import { hasCodeableConcept } from '#src/codeableConcept.ts'
import {
  ZOOM_CALL_ID_SYSTEM,
  ZOOM_CALL_LOG_ID_SYSTEM,
  ZOOM_CALL_RECORDING_ID_SYSTEM,
} from '#src/codeSystems.ts'
import { Extension } from '#src/decorators.ts'
import type { ExportFields } from '#src/ExtendedResource.ts'
import {
  COMMUNICATION_CATEGORIES,
  COMMUNICATION_MEDIUMS,
  ParticipationModeSubType,
} from '#src/models/communications/constants.ts'
import { ExtendedCommunication } from '#src/models/communications/ExtendedCommunication.ts'
import type { QueryTypes } from '#src/search.ts'
import { PHONE_COMMUNICATION } from '#src/structureDefinitions.ts'
import { isOfType } from '#src/utils.ts'
export class PhoneCommunication extends ExtendedCommunication {
  constructor({
    partOf,
    message,
    sender,
    subject,
    ...rest
  }: {
    partOf?: Reference[]
    message: string
    sender?: Reference<Practitioner>
    subject?: Reference<Patient>
  } & Partial<
    Pick<
      PhoneCommunication,
      | 'callerNumber'
      | 'receiverNumber'
      | 'isOutbound'
      | 'startTime'
      | 'endTime'
      | 'durationSeconds'
      | 'zoomCallLogId'
      | 'zoomCallId'
      | 'zoomRecordingId'
      | 'isExternal'
      | 'answerTime'
      | 'callerExtNumber'
      | 'calleeExtNumber'
      | 'callerExtType'
      | 'calleeExtType'
      | 'callResult'
      | 'isRecorded'
    >
  >) {
    super({
      partOf,
      message: message.trim(),
      sender,
      subject,
      category: COMMUNICATION_CATEGORIES.phoneCall,
      medium: [COMMUNICATION_MEDIUMS[ParticipationModeSubType.TELEPHONE]],
    })
    Object.assign(this, rest)
  }

  get zoomCallLogId(): string | undefined {
    return this.identifier(ZOOM_CALL_LOG_ID_SYSTEM)?.value
  }

  set zoomCallLogId(value: string | undefined) {
    if (value) {
      this.addIdentifier(ZOOM_CALL_LOG_ID_SYSTEM, value)
    } else {
      this.removeIdentifier(ZOOM_CALL_LOG_ID_SYSTEM)
    }
  }

  get zoomCallId(): string | undefined {
    return this.identifier(ZOOM_CALL_ID_SYSTEM)?.value
  }

  set zoomCallId(value: string | undefined) {
    if (this.zoomCallId) {
      this.markIdentifiersAsOld(ZOOM_CALL_ID_SYSTEM)
    }
    if (value) {
      this.addIdentifier(ZOOM_CALL_ID_SYSTEM, value)
    } else {
      this.removeIdentifier(ZOOM_CALL_ID_SYSTEM)
    }
  }

  get zoomRecordingId(): string | undefined {
    return this.identifier(ZOOM_CALL_RECORDING_ID_SYSTEM)?.value
  }

  set zoomRecordingId(value: string | undefined) {
    if (this.zoomRecordingId) {
      this.markIdentifiersAsOld(ZOOM_CALL_RECORDING_ID_SYSTEM)
    }
    if (value) {
      this.addIdentifier(ZOOM_CALL_RECORDING_ID_SYSTEM, value)
    } else {
      this.removeIdentifier(ZOOM_CALL_RECORDING_ID_SYSTEM)
    }
  }

  static byZoomId(zoomId: string): QueryTypes {
    return {
      identifier: { system: ZOOM_CALL_LOG_ID_SYSTEM, value: zoomId },
    }
  }

  static byZoomCallId(zoomCallId: string): QueryTypes {
    return {
      identifier: { system: ZOOM_CALL_ID_SYSTEM, value: zoomCallId },
    }
  }

  static byZoomCallRecordingId(zoomCallRecordingId: string): QueryTypes {
    return {
      identifier: {
        system: ZOOM_CALL_RECORDING_ID_SYSTEM,
        value: zoomCallRecordingId,
      },
    }
  }

  @Extension({
    url: PHONE_COMMUNICATION.CALLER_NUMBER,
    kind: 'valueString',
  })
  callerNumber?: string

  @Extension({
    url: PHONE_COMMUNICATION.RECEIVER_NUMBER,
    kind: 'valueString',
  })
  receiverNumber?: string

  @Extension({
    url: PHONE_COMMUNICATION.IS_OUTBOUND,
    kind: 'valueBoolean',
  })
  isOutbound?: boolean

  @Extension({
    url: PHONE_COMMUNICATION.START_TIME,
    kind: 'valueDateTime',
  })
  startTime?: string

  @Extension({
    url: PHONE_COMMUNICATION.END_TIME,
    kind: 'valueDateTime',
  })
  endTime?: string

  @Extension({
    url: PHONE_COMMUNICATION.DURATION_SECONDS,
    kind: 'valueInteger',
  })
  durationSeconds?: number

  @Extension({
    url: PHONE_COMMUNICATION.IS_EXTERNAL,
    kind: 'valueBoolean',
  })
  isExternal?: boolean

  @Extension({
    url: PHONE_COMMUNICATION.ANSWER_TIME,
    kind: 'valueDateTime',
  })
  answerTime?: string

  @Extension({
    url: PHONE_COMMUNICATION.CALLER_EXT_NUMBER,
    kind: 'valueString',
  })
  callerExtNumber?: string

  @Extension({
    url: PHONE_COMMUNICATION.CALLEE_EXT_NUMBER,
    kind: 'valueString',
  })
  calleeExtNumber?: string

  @Extension({
    url: PHONE_COMMUNICATION.CALLER_EXT_TYPE,
    kind: 'valueString',
  })
  callerExtType?: string

  @Extension({
    url: PHONE_COMMUNICATION.CALLEE_EXT_TYPE,
    kind: 'valueString',
  })
  calleeExtType?: string

  @Extension({
    url: PHONE_COMMUNICATION.CALL_RESULT,
    kind: 'valueString',
  })
  callResult?: string

  @Extension({
    url: PHONE_COMMUNICATION.IS_RECORDED,
    kind: 'valueBoolean',
  })
  isRecorded?: boolean

  @Extension({
    url: PHONE_COMMUNICATION.MESSAGE_SUMMARY,
    kind: 'valueString',
  })
  messageSummary?: string

  get duration(): string | undefined {
    if (!this.durationSeconds) {
      return undefined
    }
    const milliseconds = this.durationSeconds * 1000
    return format(new Date(milliseconds), 'm:ss')
  }

  get caller(): Reference<Patient | Practitioner | Organization> | undefined {
    return this.isOutbound ? this.sender : this.subject
  }

  get receiver(): Reference<Patient | Practitioner | Organization> | undefined {
    return this.isOutbound ? this.subject : this.sender
  }

  getMessageFormatted(): string {
    if (!this.message) {
      return ''
    }
    try {
      const parsed = JSON.parse(this.message)
      if (parsed.timeline) {
        return parsed.timeline
          .map((entry: { text: string }) => entry.text)
          .join('\n')
      }
      return this.message
    } catch (_) {
      return this.message
    }
  }

  static searchQuery(partOf: Reference[]): QueryTypes {
    return {
      ...ExtendedCommunication.baseSearchQuery(),
      'part-of': partOf,
      category: COMMUNICATION_CATEGORIES.phoneCall,
      _sort: '-sent',
    }
  }

  static isInstance(value: unknown): value is Communication {
    return (
      isResource(value) &&
      isOfType(value, 'Communication') &&
      hasCodeableConcept(value.category, COMMUNICATION_CATEGORIES.phoneCall)
    )
  }

  protected getExportFields(): ExportFields {
    return {
      callerId: resolveId(this.caller),
      receiverId: resolveId(this.receiver),
      callerNumber: this.callerNumber,
      receiverNumber: this.receiverNumber,
      isOutbound: this.isOutbound,
      startTime: this.startTime,
      endTime: this.endTime,
      duration: this.duration,
      durationSeconds: this.durationSeconds,
      zoomId: this.zoomCallLogId,
      zoomCallId: this.zoomCallId,
      isExternal: this.isExternal,
      answerTime: this.answerTime,
      callerExtNumber: this.callerExtNumber,
      calleeExtNumber: this.calleeExtNumber,
      callerExtType: this.callerExtType,
      calleeExtType: this.calleeExtType,
      callResult: this.callResult,
      isRecorded: this.isRecorded,
      messageSummary: this.messageSummary,
      externalResourceJSON: this.externalResourceJSON,
    }
  }

  get transcript(): {
    startTime: string
    endTime: string
    speaker: string
    text: string
  }[] {
    if (!this.externalResourceJSON) {
      return []
    }

    try {
      const parsed: ZoomTranscript = JSON.parse(this.externalResourceJSON)
      if (parsed.type !== 'zoom_transcript' || !parsed.timeline) {
        return []
      }

      return parsed.timeline.map((entry: ZoomTranscriptTimelineEntry) => {
        const speaker = entry.users?.[0]?.username ?? 'Unknown'
        const text = entry.text
        return {
          startTime: entry.ts,
          endTime: entry.end_ts,
          speaker,
          text,
        }
      })
    } catch (e) {
      console.error('Error parsing transcript JSON:', e)
      return []
    }
  }
}

interface ZoomTranscriptUser {
  username: string
  multiple_people: boolean
  user_id: string
  zoom_userid: string
  avatar_url: string
  client_type: number
  email_address: string
  channel_mark: string
  pronoun: string
  enable_speaker_diarization: string
  ts: number
  is_auto_generated: boolean
}

interface ZoomTranscriptTimelineEntry {
  text: string
  raw_text: string
  end_ts: string
  ts: string
  users: ZoomTranscriptUser[]
  userId: string
  userIds: string[]
  channelMark: string
}

interface ZoomTranscript {
  type: 'zoom_transcript'
  ver: number
  recording_id: string
  meeting_id: string
  account_id: string
  host_id: string
  recording_start: string
  recording_end: string
  timeline: ZoomTranscriptTimelineEntry[]
}
